import {
  Component,
  Input,
  Output,
  OnInit,
  OnChanges,
  SimpleChanges,
  forwardRef,
  EventEmitter,
} from '@angular/core'
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms'
import {Role} from '@app/core/services/rest-api'
import {UserService} from '@app/core/services/user/user.service'
import {filter, tap} from 'rxjs/operators'
import {ActivatedRoute} from '@angular/router'
import {ContextService} from '@app/core/services/context.service'
import {BehaviorSubject} from 'rxjs'

@Component({
  selector: 'role-selector',
  templateUrl: './role-selector.component.html',
  styleUrls: ['./role-selector.component.scss'],
  providers: [
    UserService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RoleSelectorComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class RoleSelectorComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Input() domain: string
  @Input() disabled = false
  @Input() roles: Role[] = []
  roles$ = new BehaviorSubject<Role[]>([])
  @Input() selected: Role[] = []

  @Output() changed: EventEmitter<Role[]> = new EventEmitter<Role[]>()

  private onChange: (roles: Role[]) => void
  private onTouched: () => void

  constructor(
    private userSvc: UserService,
    private route: ActivatedRoute,
    private context: ContextService,
  ) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.roles && this.roles) {
      this.roles$.next(this.roles)
    }

    if (changes.domain && this.domain) {
      this.userSvc
        .getAvailableRoles(this.domain)
        .pipe(
          filter((val) => val != null),
          tap((roles) => {
            this.roles = roles.sort((a, b) => a.id - b.id)
            this.roles$.next(this.roles)
          }),
        )
        .subscribe()
    }
  }

  isSelected(item: Role): boolean {
    return this.selected.some((e) => e.id === item.id)
  }

  toggle(role: Role): void {
    const isCurrentlySelected = this.selected.some((e) => e.id === role.id)
    this.selected = isCurrentlySelected
      ? this.selected.filter((e) => e.id !== role.id)
      : [...this.selected, role]

    this.changed.emit(this.selected) // Emit the current selection
    if (this.onChange) {
      this.onChange(this.selected) // Notify the parent component
    }
  }

  writeValue(values: Role[]): void {
    this.selected = values || []
  }

  registerOnChange(fn: (roles: Role[]) => void): void {
    this.onChange = fn // Store the provided function
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn // Store the provided function
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled
  }
}
